import { z } from 'zod';

import { makeEndpoint } from '../common';

export enum ApiKeyEntityType {
  BRAND = 'brand',
  ORGANIZATION = 'organization',
}

/**
 * Secret keys are server-side only. Publishable keys identify a brand from a browser
 * and are additionally constrained to the brand's approved origins.
 */
export enum ApiKeyType {
  SECRET = 'secret',
  PUBLISHABLE = 'publishable',
}

export enum ApiKeyEnvironment {
  LIVE = 'live',
  TEST = 'test',
}

export const ApiKeySchema = z.object({
  id: z.string(),
  brandId: z.string().nullable(),
  organizationId: z.string().nullable(),
  token: z.string(),
  name: z.string().nullable(),
  // Derived from the token; null for keys issued before the way_ format.
  keyType: z.nativeEnum(ApiKeyType).nullable().default(null),
  environment: z.nativeEnum(ApiKeyEnvironment).nullable().default(null),
});

export const GetApiKeysResponseSchema = z.object({ items: z.array(ApiKeySchema) });

export type GetApiKeysResponse = z.infer<typeof GetApiKeysResponseSchema>;

export const PostApiKeysRequestSchema = z.object({
  name: z.string().nullable().default(null),
  keyType: z.nativeEnum(ApiKeyType).default(ApiKeyType.SECRET),
});

export type PostApiKeysRequest = z.infer<typeof PostApiKeysRequestSchema>;

export const PostApiKeysResponseSchema = ApiKeySchema;

export type PostApiKeysResponse = z.infer<typeof ApiKeySchema>;

export type ApiKey = z.infer<typeof ApiKeySchema>;

export const GetApiKeysEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ entityType, entityId }: { entityType: ApiKeyEntityType; entityId: string }) =>
    `v1/${entityType}/${entityId}/api-keys`,
  requestSchema: z.void(),
  responseSchema: GetApiKeysResponseSchema,
});

export const PostApiKeyEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ entityType, entityId }: { entityType: ApiKeyEntityType; entityId: string }) =>
    `v1/${entityType}/${entityId}/api-keys`,
  requestSchema: PostApiKeysRequestSchema,
  responseSchema: PostApiKeysResponseSchema,
});

export const DeleteApiKeyEndpoint = makeEndpoint({
  method: 'DELETE',
  url: ({ entityType, entityId, apiKeyId }: { entityType: ApiKeyEntityType; entityId: string; apiKeyId: string }) =>
    `v1/${entityType}/${entityId}/api-keys/${apiKeyId}`,
  requestSchema: z.void(),
  responseSchema: z.void(),
});
